import { Component, Input, effect, inject, signal } from "@angular/core";
import { SERIE_REPOSITORY } from "@app/app.providers";
import { VisualizationComponent } from "@app/shared/components/visualization/visualization.component";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { DeviceService } from "@app/shared/services/device.service";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { DateUtils } from "@arch/_shared/utils";
import {
    CompanyEntity,
    SerieEntity,
    VisualizationEntity,
    VisualizationType
} from "@arch/domain/entity";
import { ISerieRepository } from "@arch/domain/repository";
import { PopulateDrillDownController } from "@arch/presentation/controllers/populate-drilldown.controller";
import { PopulateDrillerController } from "@arch/presentation/controllers/populate-driller.controller";
import _ from "lodash";

@Component({
    selector: "app-drilldown",
    imports: [SharedMaterialModule, SharedDsnModule, VisualizationComponent],
    templateUrl: "./drilldown.component.html",
    styleUrl: "./drilldown.component.scss"
})
export class DrilldownComponent {
    @Input({ required: true }) set options(value: VisualizationOptionsType) {
        this.optionsSignal.set(value);
    }
    get options() {
        return this.optionsSignal();
    }
    private optionsSignal = signal<VisualizationOptionsType>(null);
    @Input({ required: true }) company!: CompanyEntity;
    @Input({ required: true }) visualization!: VisualizationEntity;
    @Input({ required: false }) loading: boolean;
    @Input({ required: false }) pdf: boolean;
    @Input({ required: false }) handleSelectVisualization: (
        visualization: VisualizationEntity
    ) => void;

    private readonly serieRepository = inject(
        SERIE_REPOSITORY
    ) as ISerieRepository;
    private readonly populateDrillDownController = inject(
        PopulateDrillDownController
    );
    private readonly populateDrillerController = inject(
        PopulateDrillerController
    );

    public drilldownVisualizations: {
        options: VisualizationOptionsType;
        visualization: VisualizationEntity;
        suffix?: string;
    }[] = [];
    public breadcrumb: VisualizationEntity[] = [];
    public isAccumulated: boolean = false;
    public loadingDrilldown: boolean = true;
    private preserveBreadcrumb: boolean = false;
    private readonly deviceService = inject(DeviceService);
    public isMobile = this.deviceService.getIsMobile();

    constructor() {
        effect(() => {
            if (!this.preserveBreadcrumb) this.breadcrumb = [];
            if (!this.options || this.loading) return;
            if (!this.preserveBreadcrumb)
                this.addBreadcrumb(this.visualization);
            const seriesIds = this.visualization.getDrilldownSeries();
            this.setDrilldownSeries(seriesIds);
        });
    }

    private addBreadcrumb(visualization: VisualizationEntity): void {
        this.breadcrumb.push(visualization);
    }

    private async setDrilldownSeries(seriesIds: string[]): Promise<
        {
            options: VisualizationOptionsType;
            visualization: VisualizationEntity;
        }[]
    > {
        this.loadingDrilldown = true;
        this.drilldownVisualizations = [];
        if (!seriesIds.length) {
            return this.drilldownVisualizations;
        }
        const series = this.getSeriesSorted(seriesIds);
        if (!this.isAccumulated) {
            this.isAccumulated = series.some(
                (serie) => serie.props.metadata?.accumulated
            );
        }
        const drilldownSeries = await this.getDrilldownSeries(series);
        this.createDrilldownVisualizations(drilldownSeries);
        setTimeout(() => {
            this.loadingDrilldown = false;
        }, 500);
        return this.drilldownVisualizations;
    }

    private getSeriesSorted(seriesIds: string[]): SerieEntity[] {
        const series = this.serieRepository.getByIds(seriesIds);
        return series.sort((a, b) => {
            return (
                seriesIds.indexOf(a.props._id) - seriesIds.indexOf(b.props._id)
            );
        });
    }

    private async getDrilldownSeries(
        series: SerieEntity[]
    ): Promise<SerieEntity[]> {
        const filteredSeries = series.filter(
            (s) => s.props.type !== "constant"
        );
        if (filteredSeries.length > 1) return filteredSeries;
        const mainSerie = series[0];
        if (!mainSerie) return [];
        if (mainSerie.isAssociatedRaw())
            return await this.populateDriller(mainSerie);
        const compounds = series
            .map((serie) => serie.props.compound)
            .flat()
            .filter(Boolean);
        if (!compounds.length) return [];
        await this.populate(compounds);
        return this.getSeriesSorted(compounds);
    }

    private async populateDriller(serie: SerieEntity): Promise<SerieEntity[]> {
        const response = await this.populateDrillerController.execute({
            company: this.company,
            periodOptions: this.options,
            seriesIds: serie.props.associatedRaw || serie.props.compound
        });
        return response?.series || [];
    }

    private async populate(seriesIds: string[]): Promise<void> {
        await this.populateDrillDownController.execute({
            company: this.company,
            periodOptions: this.options,
            seriesIds,
            options: {
                preserveMap: true
            }
        });
    }

    public async onVisualizationClick(
        visualization: VisualizationEntity
    ): Promise<void> {
        this.preserveBreadcrumb = true;

        this.addBreadcrumb(visualization);
        const seriesIds = visualization.getDrilldownSeries();
        await this.setDrilldownSeries(seriesIds);
        this.handleSelectVisualization(visualization);
    }

    public async onBreadcrumbClick(index: number): Promise<void> {
        if (index === this.breadcrumb.length - 1) return;
        this.breadcrumb = this.breadcrumb.slice(0, index + 1);
        const seriesIds = this.breadcrumb[index].getDrilldownSeries();
        await this.setDrilldownSeries(seriesIds);
        this.handleSelectVisualization(this.breadcrumb[index]);
    }

    private createDrilldownVisualizations(series: SerieEntity[]): void {
        const validSeries = this.filterValidSeries(series);

        for (const serie of validSeries) {
            const visualization = this.createVisualizationEntity(serie);
            if (!visualization) continue;

            this.addVisualizationBasedOnOptions(visualization);
        }
    }

    private filterValidSeries(series: SerieEntity[]): SerieEntity[] {
        return series.filter((serie) => !serie.props.metadata?.disabled);
    }

    private addVisualizationBasedOnOptions(
        visualization: VisualizationEntity
    ): void {
        if (this.options.budget) {
            this.createBudget(visualization);
        } else if (this.options.comparation.allMonths) {
            this.createAllMonths(visualization);
        } else if (this.options.comparation.active) {
            this.createComparation(visualization);
        } else {
            this.addBaseVisualization(visualization);
        }
    }

    private addBaseVisualization(visualization: VisualizationEntity): void {
        this.drilldownVisualizations.push({
            options: this.options,
            visualization
        });
    }

    private createComparation(visualization: VisualizationEntity): void {
        const baseOptions = this.createBaseComparationOptions();
        const comparationOptions = this.createComparationOptions();

        this.drilldownVisualizations.push(
            {
                options: baseOptions,
                visualization,
                suffix: " - Base"
            },
            {
                options: comparationOptions,
                visualization,
                suffix: " - Comparado"
            }
        );
    }

    private createBaseComparationOptions(): VisualizationOptionsType {
        const options = structuredClone(this.options);
        options.comparation.active = false;
        options.comparation.allMonths = false;
        options.dates = DateUtils.projectMonths(
            [options.endDate],
            12,
            "backward"
        );
        options.startDate = options.dates[options.dates.length - 1];
        return options;
    }

    private createComparationOptions(): VisualizationOptionsType {
        const options = structuredClone(this.options);
        options.comparation.active = false;
        options.comparation.allMonths = false;
        options.endDate = options.comparation.date;
        options.scenario = options.comparation.scenario;
        options.dates = DateUtils.projectMonths(
            [options.comparation.date],
            12,
            "backward"
        );
        options.startDate = options.dates[options.dates.length - 1];
        options.comparation.date = DateUtils.getPreviousMonthByPeriod(
            options.comparation.date,
            options.comparation.period
        );
        return options;
    }

    private createBudget(visualization: VisualizationEntity): void {
        const baseOptions = _.cloneDeep(this.options);
        baseOptions.budget = "";
        const comparationOptions = _.cloneDeep(this.options);
        comparationOptions.scenario.props._id = comparationOptions.budget;
        comparationOptions.comparation.scenario.props._id =
            comparationOptions.budget;
        this.drilldownVisualizations.push(
            {
                options: baseOptions,
                visualization
            },
            {
                options: comparationOptions,
                visualization,
                suffix: " - Orçamento"
            }
        );
    }

    private createAllMonths(visualization: VisualizationEntity): void {
        const baseOptions = this.createBaseAllMonthsOptions();
        const comparationOptions = this.createComparationAllMonthsOptions();
        this.drilldownVisualizations.push(
            {
                options: baseOptions,
                visualization,
                suffix: " - Base"
            },
            {
                options: comparationOptions,
                visualization,
                suffix: " - Comparado"
            }
        );
    }

    private createBaseAllMonthsOptions(): VisualizationOptionsType {
        const options = structuredClone(this.options);
        options.startDate = new Date(
            `${options.endDate.getUTCFullYear()}/01/01`
        );
        options.endDate = new Date(`${options.endDate.getUTCFullYear()}/12/01`);
        options.comparation.active = false;
        options.comparation.allMonths = false;
        options.dates = DateUtils.getAllMonthsOfYears([
            options.endDate.getUTCFullYear()
        ]);
        return options;
    }

    private createComparationAllMonthsOptions(): VisualizationOptionsType {
        const options = structuredClone(this.options);
        options.startDate = new Date(
            `${options.comparation.date.getUTCFullYear()}/01/01`
        );
        options.endDate = new Date(
            `${options.comparation.date.getUTCFullYear()}/12/01`
        );
        options.comparation.date = new Date(
            `${options.comparation.date.getUTCFullYear()}/11/01`
        );
        options.comparation.active = false;
        options.comparation.allMonths = false;
        options.dates = DateUtils.getAllMonthsOfYears([
            options.comparation.date.getUTCFullYear()
        ]);
        options.scenario = options.comparation.scenario;
        return options;
    }

    private createVisualizationEntity(
        serie: SerieEntity
    ): VisualizationEntity | null {
        const source = serie.getDrillDownSource(this.options.source);
        const affiliate = this.company.getAffiliateBySerieRef(serie);
        const visualizationData: VisualizationType = {
            accountingId: this.company.props.accountingId,
            companyId: this.company.props._id,
            name: serie.props.name,
            historic: [{ dataSerieId: serie.props._id }],
            series: [serie.props._id],
            sources: [source]
        };
        const visualization = VisualizationEntity.restoreDrilldown(
            visualizationData,
            {
                chartHistoricCategory: "areaspline",
                affiliate
            }
        );
        visualization.setPDFMode(this.pdf);
        return visualization;
    }
}
